import { Booking } from "PFTypes";
import { Dispatch, RefObject, SetStateAction, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import {
  BookingDataItem,
  BookingFormData,
  BookingFormDataCreateMode,
  BookingFormMode
} from "../booking_form.types";
import { BookingFormValues } from "../booking_form_values.types";
import { useInitialBookingsForCreate } from "./use_initial_bookings_for_create";
import { useInitialBookingsForEditOrClone } from "./use_initial_bookings_for_edit_or_clone";

type UseBookingInitialData = {
  initialData: BookingFormData;
  mode: BookingFormMode;
  booking?: Booking;
  loadingState: Record<string, boolean>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  initialBookingsRef: RefObject<BookingDataItem[] | null>;
};
type UseBookingInitialDataReturn = {
  loading: boolean;
};

export const useInitialBookings = ({
  initialData = {},
  mode,
  booking,
  loadingState,
  setLoadingState,
  initialBookingsRef
}: UseBookingInitialData): UseBookingInitialDataReturn => {
  const { setValue } = useFormContext<BookingFormValues>();

  const isCreateMode = mode === BookingFormMode.Create;
  const isCloneMode = [BookingFormMode.CloneSingle, BookingFormMode.CloneAll].includes(mode);

  const {
    bookings: initialBookingsForCreate,
    globalTitle,
    globalDescription: globalDescriptionForCreate,
    loading: initialBookingsForCreateLoading
  } = useInitialBookingsForCreate({
    initialData: initialData as BookingFormDataCreateMode,
    enabled: isCreateMode,
    loadingState,
    setLoadingState
  });

  const { bookings: initialBookingsForEditOrClone, loading: initialBookingsForEditOrCloneLoading } =
    useInitialBookingsForEditOrClone({
      initialBooking: booking,
      enabled: !isCreateMode,
      mode,
      loadingState,
      setLoadingState
    });

  const bookings = isCreateMode ? initialBookingsForCreate : initialBookingsForEditOrClone;
  const loading = initialBookingsForCreateLoading || initialBookingsForEditOrCloneLoading;

  const globalDescriptionForClone = isCloneMode ? booking?.description : undefined;

  const globalDescription = globalDescriptionForClone ?? globalDescriptionForCreate;

  useEffect(() => {
    setValue("bookings", bookings);
  }, [bookings]);

  useEffect(() => {
    if (isCreateMode || isCloneMode) {
      setValue("globalTitle", globalTitle);
      setValue("globalDescription", globalDescription);
    }
  }, [globalTitle, globalDescription]);

  useEffect(() => {
    initialBookingsRef.current = bookings;
  }, [bookings, initialBookingsRef]);

  return {
    loading
  };
};
